import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import PremiumNudge from '../components/PremiumNudge';
import type { PremiumFeatureKey } from '../components/PremiumNudge';
import { hasTierAccess } from '../services/accessControl';
import type { AppUser } from '../types';

interface PremiumNudgeContextValue {
  showNudge: (featureKey: PremiumFeatureKey) => void;
}

const PremiumNudgeContext = createContext<PremiumNudgeContextValue>({ showNudge: () => {} });

export const usePremiumNudge = () => useContext(PremiumNudgeContext).showNudge;

const NUDGE_COOLDOWN_MS = 60_000;
const NUDGE_AUTO_DISMISS_MS = 6_000;

export interface PremiumNudgeHandle {
  showNudge: (featureKey: PremiumFeatureKey) => void;
}

interface ProviderProps {
  children: React.ReactNode;
  onUpgrade: () => void;
  onLogin: () => void;
  isGuest: boolean;
  currentUser: AppUser | null;
  isPaidUser: boolean;
  isPremiumUser: boolean;
  hasPremiumAccess: (user: AppUser | null, isDev?: boolean) => boolean;
  userTier?: number;
}

export const PremiumNudgeProvider = forwardRef<PremiumNudgeHandle, ProviderProps>(
  ({ children, onUpgrade, onLogin, isGuest, currentUser, isPaidUser, isPremiumUser, hasPremiumAccess, userTier }, ref) => {
    const [activeNudge, setActiveNudge] = useState<PremiumFeatureKey | null>(null);
    const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const showNudge = useCallback((featureKey: PremiumFeatureKey) => {
      if (isPremiumUser || hasPremiumAccess(currentUser, false)) return;
      // Sjekk om brukerens tier gir tilgang til denne funksjonen
      if (hasTierAccess({ tier: userTier || 0 }, featureKey)) return;
      if (isPaidUser) return;

      const cooldownKey = `fricamp_nudge_${featureKey}`;
      try {
        const lastShown = sessionStorage.getItem(cooldownKey);
        if (lastShown && Date.now() - Number(lastShown) < NUDGE_COOLDOWN_MS) return;
        sessionStorage.setItem(cooldownKey, String(Date.now()));
      } catch { /* sessionStorage unavailable */ }

      setActiveNudge(featureKey);

      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = setTimeout(() => setActiveNudge(null), NUDGE_AUTO_DISMISS_MS);
    }, [currentUser, hasPremiumAccess, isPaidUser, isPremiumUser, userTier]);

    const handleDismiss = useCallback(() => {
      setActiveNudge(null);
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    }, []);

    const handleUpgrade = useCallback(() => {
      handleDismiss();
      if (isGuest) {
        onLogin();
      } else {
        onUpgrade();
      }
    }, [isGuest, onLogin, onUpgrade, handleDismiss]);

    useImperativeHandle(ref, () => ({ showNudge }), [showNudge]);

    useEffect(() => {
      return () => { if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current); };
    }, []);

    return (
      <PremiumNudgeContext.Provider value={{ showNudge }}>
        {children}
        <PremiumNudge
          featureKey={activeNudge}
          onUpgrade={handleUpgrade}
          onDismiss={handleDismiss}
          isGuest={isGuest}
        />
      </PremiumNudgeContext.Provider>
    );
  }
);

PremiumNudgeProvider.displayName = 'PremiumNudgeProvider';
